import { EventEmitter, Injectable } from "@angular/core"
import { Observable, of } from "rxjs"
import { catchError, map } from "rxjs/operators"
import { environment } from "../../environments/environment"
import { HelpersService } from "./helpers.service"
import { MapMarker } from "../models/marker"
import {
	icon_building_ftth_blue,
	icon_building_ftth_blue_dark,
	icon_building_ftth_green,
	icon_building_ftth_pink,
	icon_cluster_blue,
	icon_cluster_green,
	icon_cluster_pink,
	ico_active,
	ico_active_p2p,
	ico_interest_point,
	ico_interest_point_smaller,
	ico_picked,
	ico_picked_p2p,
	ico_pop_naz,
	ico_pop_naz_smaller,
	ico_preview
} from "src/configs/map_configuration"
import { pageConfiguration } from "src/configs/page_configuration"
import { HttpClient } from "@angular/common/http"
import { Router } from "@angular/router"

@Injectable()
export class MapService extends HelpersService {
	public mapReadyEvent = new EventEmitter()
	public searchModes = environment.LOCAL
		? [
				{ value: "address", viewValue: this.instant("SEARCH_METHOD.ADDRESS") },
				{ value: "id_building", viewValue: this.instant("SEARCH_METHOD.ID_BUILDING") }
			]
		: []
	public selectedCluster = null
	public mapButtons = {
		pops: {
			active: false,
			tooltip: "BUTTONS.POP",
			icon: "./assets/imgs/markers/ico-pop-active.png?v={{assets_version}}",
			visible: false,
			title: "POP"
		},
		pcn: {
			active: false,
			tooltip: "BUTTONS.PCN",
			icon: "./assets/imgs/markers/ico-cd-map.png?v={{assets_version}}",
			visible: false,
			title: "PCN"
		},
		pop_agf: {
			active: false,
			tooltip: "BUTTONS.BI1G",
			icon: "./assets/imgs/markers/ico-agf-map.png?v={{assets_version}}",
			visible: false,
			title: "BI1G"
		},
		pop_all: {
			active: false,
			tooltip: "POP / PCN / BI1G", // TODO to enable AGF: 'POP / PCN / AGF'
			icon: "./assets/imgs/markers/ico-cd-map.png?v={{assets_version}}",
			visible: false,
			title: "ALL"
		},
		pop_pcn: {
			active: false,
			tooltip: "BUTTONS.POP_PCN",
			icon: "./assets/imgs/markers/ico-cd-map.png?v={{assets_version}}",
			visible: false,
			title: "POP PCN"
		},
		poi: {
			active: false,
			tooltip: "BUTTONS.POI",
			icon: "./assets/imgs/markers/ico-Point-Of-Interest-active.png?v={{assets_version}}",
			visible: false
		},
		covered_ab: {
			active: true,
			tooltip: "BUTTONS.COVERED_AB",
			icon: "./assets/imgs/markers/ico-mappa-copertura-active.png?v={{assets_version}}",
			visible: false
		},
		covered_cd: {
			active: false,
			tooltip: "BUTTONS.COVERED_CD",
			icon: "./assets/imgs/markers/ico-cd-map.png?v={{assets_version}}",
			visible: false
		},
		covered_agf: {
			active: false,
			tooltip: "BUTTONS.COVERED_AGF",
			icon: "./assets/imgs/markers/ico-agf-map.png?v={{assets_version}}", // TODO make icon
			visible: false
		}
	}

	constructor(http: HttpClient, router: Router) {
		super(http, router)
	}

	public pops: any
	public pois: any

	public get activeButtons() {
		const buttons = []
		if (this.mapButtons.covered_ab && this.mapButtons.covered_ab.active) {
			buttons.push("CoveredAB")
		}
		if (this.mapButtons.covered_cd && this.mapButtons.covered_cd.active) {
			buttons.push("CoveredCD")
			if (this.activePayload === "open_stream_cd") {
				buttons.push("CoveredFWA")
			}
		}
		if (this.mapButtons.covered_agf.active) {
			buttons.push("CoveredAGF")
		}
		return buttons
	}

	getPops(olocode?): Observable<any> {
		if (this.pops) {
			return of(this.pops)
		} else {
			let params = "?"
			if (this.activePayload == "pre_provisioning_kit" || this.activePayload == "pre_provisioning_svlan") {
				params += "is_registered=true"
				if (olocode) {
					params += `&olo_code=${olocode}`
				} else {
					params += ""
				}
			}
			return this.http
				.get(environment.DOMAIN_URL + "/api2/consumer-map/pops" + params, this.getHTTPHeader())
				.pipe(
					map(data => {
						this.pops = data
						return data
					}),
					catchError(error => {
						return this.handleError(error, "Pop")
					})
				)
		}
	}

	getPopById(id): Observable<any> {
		return this.http.get(environment.DOMAIN_URL + `/api2/marketing/pops/${id}`, this.getHTTPHeader()).pipe(
			map(data => {
				return data
			}),
			catchError(error => {
				return this.handleError(error, "Pop")
			})
		)
	}

	searchBuildingPlaceholders(
		bounds: google.maps.LatLngBounds,
		zoom,
		activeButtons,
		overrideCluster?,
		serviceName?
	): Observable<any> {
		let typeParams = ""
		if (overrideCluster) {
			typeParams += "&types[]=Covered" + overrideCluster.toUpperCase()
		} else if (activeButtons && !overrideCluster) {
			for (const p of activeButtons) {
				typeParams += "&types[]=" + p
			}
		}
		let serviceNameParams = ""
		if (serviceName) {
			serviceNameParams += "&service_name=" + serviceName
		}
		const buildingParams = pageConfiguration[this.activePayload].saleableBuildings
		const bbox =
			bounds.getSouthWest().lng() +
			"," +
			bounds.getSouthWest().lat() +
			"," +
			bounds.getNorthEast().lng() +
			"," +
			bounds.getNorthEast().lat()
		const url =
			environment.DOMAIN_URL +
			"/api2/consumer-map/buildings" +
			"?in_bbox=" +
			bbox +
			"&zoom=" +
			zoom +
			typeParams +
			"&building_statuses=" +
			buildingParams +
			serviceNameParams
		return this.http.get<any>(url, this.getHTTPHeader()).pipe(
			map(data => {
				return data
			}),
			catchError(error => {
				return this.handleError(error, "NumeroCivico")
			})
		)
	}

	getBuildingById(id, activeButtons, overrideCluster?, serviceName?): Observable<any> {
		let typeParams = ""
		if (overrideCluster) {
			typeParams += "&types[]=Covered" + overrideCluster.toUpperCase()
		} else if (activeButtons && !overrideCluster) {
			for (const p of activeButtons) {
				typeParams += "&types[]=" + p
			}
		}
		let serviceNameParams = ""
		if (serviceName) {
			serviceNameParams += "&service_name=" + serviceName
		}
		return this.http
			.get<any>(
				`${environment.DOMAIN_URL}/api2/consumer-map/buildings/of?building_id=${id}` +
					typeParams +
					serviceNameParams,
				this.getHTTPHeader()
			)
			.pipe(
				map(data => {
					return data
				}),
				catchError(error => {
					return this.handleError(error, "Building")
				})
			)
	}

	getBuildingByCoordinates(lat, lng, params, serviceName): Observable<any> {
		let typeParams = ""
		for (let p of params) {
			typeParams += "&types[]=" + p
		}
		let serviceNameParams = ""
		if (serviceName) {
			serviceNameParams += "&service_name=" + serviceName
		}
		return this.http
			.get<any>(
				`${environment.DOMAIN_URL}/api2/consumer-map/buildings/coordinates-search?lng=${lng}&lat=${lat}${typeParams}${serviceNameParams}`,
				this.getHTTPHeader()
			)
			.pipe(
				map(data => {
					return data
				}),
				catchError(error => {
					return this.handleError(error, "Building")
				})
			)
	}

	public pointLabel(results: google.maps.GeocoderResult[]) {
		return results && results[0] ? results[0].formatted_address : ""
	}

	public pointInfo(results: google.maps.GeocoderResult[]) {
		const ret: any = {}
		ret.comune = this.getInfo(results, "administrative_area_level_3")
		ret.cap = this.getInfo(results, "postal_code")
		ret.country = this.getInfo(results, "country", "short_name")
		ret.street_number = this.getInfo(results, "street_number", "short_name")
		ret.street = this.getInfo(results, "route") // TODO town square
		ret.civic_number = this.getInfo(results, "street_number")
		ret.label = results && results[0] ? results[0].formatted_address : ""
		return ret
	}

	public getInfo(results, type, attr = "long_name") {
		let ret = ""
		if (results && results.length) {
			for (const result of results) {
				const found = result.address_components.filter(a => a.types[0] === type)[0]
				if (found) {
					ret = found[attr]
					return ret
				} else {
					ret = ""
				}
			}
		}
		return ret
	}

	// Markers model

	public popToMarker(pop, iconNormal?, iconSmaller?) {
		let marker: MapMarker
		const range = { min: 0, max: null }
		if (!pop.show) {
			range.min = null
		} else if (pop.show && !pop.nazionale) {
			range.min = 9
		}
		let area_type
		let type
		if (pop.type === "Secondary") {
			area_type = "covered_cd"
			type = "PCN"
		} else if (pop.type !== "Secondary" && pop.type !== "AGF") {
			area_type = "covered_ab"
			type = "POP"
		} else if (pop.type === "AGF") {
			area_type = "covered_agf"
			type = "POP_AGF"
		}
		marker = {
			area_type,
			icons: [
				{
					area_type: "covered_cd",
					zoom_from: 1,
					zoom_to: 11,
					path: iconSmaller ? iconSmaller : ico_pop_naz_smaller
				},
				{ area_type: "covered_cd", zoom_from: 12, zoom_to: 30, path: iconNormal ? iconNormal : ico_pop_naz },
				{
					area_type: "covered_ab",
					zoom_from: 1,
					zoom_to: 11,
					path: iconSmaller ? iconSmaller : ico_pop_naz_smaller
				},
				{ area_type: "covered_ab", zoom_from: 12, zoom_to: 30, path: iconNormal ? iconNormal : ico_pop_naz },
				{
					area_type: "covered_agf",
					zoom_from: 1,
					zoom_to: 11,
					path: iconSmaller ? iconSmaller : ico_pop_naz_smaller
				}, // TODO icona
				{ area_type: "covered_agf", zoom_from: 12, zoom_to: 30, path: iconNormal ? iconNormal : ico_pop_naz } // TODO icona
			],
			type,
			coordinates: { lat: pop.lat, lng: pop.lng },
			id: pop.id,
			label: pop.alias ? pop.alias : pop.nome,
			range: range,
			nome: pop.nome ? pop.nome : "",
			alias_pop: pop.alias ? pop.alias : "",
			can_hover: true,
			can_click: true
		}
		return marker
	}

	public poiToMarker(poi, iconNormal?, iconSmaller?) {
		let marker: MapMarker
		marker = {
			area_type: null,
			icons: [
				{ zoom_from: 1, zoom_to: 11, path: iconSmaller ? iconSmaller : ico_interest_point_smaller },
				{ zoom_from: 12, zoom_to: 30, path: iconNormal ? iconNormal : ico_interest_point }
			],
			type: "POI",
			coordinates: poi.coordinates,
			id: poi.id,
			label: poi.address,
			range: { min: 0, max: null },
			can_hover: true,
			can_click: true
		}
		return marker
	}

	// Click on map
	public picked2Marker(picked) {
		let marker: MapMarker
		marker = {
			area_type: null,
			type: "PICKED",
			coordinates: { lat: picked.latLng.lat(), lng: picked.latLng.lng() },
			id: null,
			label: picked.label,
			range: { min: 0, max: null },
			can_hover: false,
			can_click: false,
			icons: [{ zoom_from: 1, zoom_to: 30, path: ico_preview }]
		}
		return marker
	}

	// Address search
	public place2Marker(place, type) {
		let marker: MapMarker
		let pathIcon
		switch (type) {
			case "POINT_PLACE":
				pathIcon = ico_picked
				break
			case "POINT_PLACE_A":
				pathIcon = ico_picked_p2p
				break
			case "POINT_PLACE_B":
				pathIcon = ico_picked
				break
		}
		marker = {
			area_type: null,
			type: type,
			coordinates: { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() },
			id: null,
			label: place.formatted_address,
			range: { min: 0, max: null },
			can_hover: false,
			can_click: true,
			icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }]
		}
		return marker
	}

	// Toast, civici
	public marker2MarkerPoint(point: MapMarker, type, altIcon?) {
		let marker: MapMarker
		let pathIcon
		switch (type) {
			case "POINT_PICKED":
				pathIcon = ico_picked
				break
			case "POINT_PICKED_A":
				pathIcon = ico_picked_p2p
				break
			case "POINT_PICKED_B":
				pathIcon = ico_picked
				break
			case "POINT_CIVIC":
				pathIcon = ico_active
				break
			case "POINT_CIVIC_A":
				pathIcon = ico_active_p2p
				break
			case "POINT_CIVIC_B":
				pathIcon = ico_active
				break
		}
		marker = {
			area_type: null,
			type: type,
			coordinates: point.coordinates,
			id: point.id,
			label: point.label,
			range: { min: 0, max: null },
			can_hover: false,
			can_click: false,
			icons: [{ zoom_from: 1, zoom_to: 30, path: altIcon ? altIcon : pathIcon }],
			marker_details: point.marker_details
		}
		return marker
	}

	public cluster2Marker(cluster) {
		let pathIcon
		let marker: any = {
			coordinates: { lat: cluster.coordinates.coordinates[1], lng: cluster.coordinates.coordinates[0] },
			id: null,
			can_hover: false,
			can_click: true,
			label: String(cluster.tot_buildings),
			range: { min: 0, max: null },
			label_inside: true,
			type: "cluster"
		}
		switch (cluster.type) {
			case "CoveredAB":
				pathIcon = icon_cluster_pink
				marker = {
					...marker,
					area_type: "covered_ab",
					icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }]
				}
				break
			case "CoveredCD":
				pathIcon = icon_cluster_green
				marker = {
					...marker,
					area_type: "covered_cd",
					icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }]
				}
				break
			case "CoveredAGF":
				pathIcon = icon_cluster_green // TODO icona
				marker = {
					...marker,
					area_type: "covered_agf",
					icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }]
				}
				break
			case "CoveredFWA":
				pathIcon = icon_cluster_green
				marker = {
					...marker,
					area_type: "covered_fwa",
					icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }]
				}
				break
			case "Designed":
				pathIcon = icon_cluster_blue
				marker = {
					...marker,
					area_type: "prospect",
					icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }]
				}
				break
		}
		return marker
	}

	public civic2Marker(civic) {
		let marker: any = {
			coordinates: { lat: civic.coordinates.coordinates[1], lng: civic.coordinates.coordinates[0] },
			id: civic.id_building,
			can_hover: true,
			can_click: true,
			type: civic.tipologia_building,
			label: civic.building_labels.join("<br/>"),
			range: { min: 18, max: 30 },
			marker_details: {
				status: civic.stato_building,
				description: civic.description,
				afference_pop: civic.afference_pop,
				address: civic.building_labels.join("<br/>"),
				id: civic.id,
				regione: civic.indirizzo.regione,
				provincia: civic.indirizzo.provincia,
				comune: civic.indirizzo.comune,
				via: civic.indirizzo.via,
				civico: civic.indirizzo.civico,
				particella: civic.indirizzo.particella_top
			}
		}
		let pathIcon
		switch (civic.type) {
			case "CoveredAB":
				pathIcon = icon_building_ftth_pink
				marker = {
					...marker,
					area_type: "covered_ab",
					icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }]
				}
				break
			case "CoveredCD":
				pathIcon = icon_building_ftth_green
				marker = {
					...marker,
					area_type: "covered_cd",
					icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }]
				}
				break
			case "CoveredAGF":
				pathIcon = icon_building_ftth_pink // TODO icona
				marker = {
					...marker,
					area_type: "covered_agf",
					icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }]
				}
				break
			case "CoveredFWA":
				pathIcon = icon_building_ftth_green
				marker = {
					...marker,
					area_type: "covered_fwa",
					icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }]
				}
				break
			case "Designed":
				pathIcon = icon_building_ftth_blue
				marker = {
					...marker,
					area_type: "prospect",
					icons: [{ zoom_from: 1, zoom_to: 30, path: pathIcon }]
				}
				break
		}
		return marker
	}
}
